import { useCallback, useEffect, useState } from "react";
import { researchCms } from "@/api/researchCms.service";
import { SearchModal, searchModalStyles } from "../SearchModal/SearchModal";
import styles from "./AddBpaModal.module.css";

type ExistingBpa = {
  id: string;
  name: string;
  rankingEntityName: string;
  alreadyLinked: boolean;
};

type Publication = {
  name: string;
  directoryId: string;
};

type AddBpaModalProps = {
  open: boolean;
  onClose: () => void;
  publications: Publication[];
  currentDirectoryId?: string | null;
  linkedBpaIds: Set<string>;
  onLink: (bpaId: string) => Promise<void>;
  onCreateNew: () => void;
};

export function AddBpaModal({
  open,
  onClose,
  publications,
  currentDirectoryId,
  linkedBpaIds,
  onLink,
  onCreateNew,
}: AddBpaModalProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ExistingBpa[]>([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDirectoryId, setSelectedDirectoryId] = useState("");

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
      setError(null);
    } else {
      setSelectedDirectoryId(currentDirectoryId ?? "");
    }
  }, [open, currentDirectoryId]);

  const search = useCallback(
    async (term: string) => {
      setSearching(true);
      try {
        const data = await researchCms.getBpas({
          search: term,
          limit: 30,
          ...(selectedDirectoryId ? { directoryId: selectedDirectoryId } : {}),
        });
        setResults(
          data.researchBpas.map(bpa => ({
            id: bpa.id,
            name: bpa.name,
            rankingEntityName: bpa.rankingEntityName,
            alreadyLinked: linkedBpaIds.has(bpa.id),
          })),
        );
      } catch (err) {
        console.error("BPA search failed:", err);
        setResults([]);
      } finally {
        setSearching(false);
      }
    },
    [linkedBpaIds, selectedDirectoryId],
  );

  useEffect(() => {
    if (!open) return;
    search(query);
  }, [open, search]);

  async function handleLink(bpaId: string) {
    setLinking(true);
    setError(null);
    try {
      await onLink(bpaId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to link practice area");
    } finally {
      setLinking(false);
    }
  }

  const available = results.filter(r => !r.alreadyLinked);
  const linked = results.filter(r => r.alreadyLinked);

  return (
    <SearchModal
      open={open}
      onClose={onClose}
      title="Add practice area"
      query={query}
      onQueryChange={setQuery}
      onSearch={search}
      debounceMs={300}
      placeholder="Search existing practice areas..."
      searching={searching}
      hasResults={results.length > 0}
      resultCount={results.length}
      idleMessage="Search for an existing practice area"
      idleHint="Or create a new one below"
      emptyMessage={`No practice areas found for "${query}"`}
      error={error}
      filterSlot={
        publications.length > 1 ? (
          <div className={styles.publicationFilter}>
            <label className={styles.publicationLabel} htmlFor="add-bpa-publication">
              Publication
            </label>
            <select
              id="add-bpa-publication"
              className={styles.publicationSelect}
              value={selectedDirectoryId}
              onChange={e => setSelectedDirectoryId(e.target.value)}
            >
              <option value="">All publications</option>
              {publications.map(p => (
                <option key={p.directoryId} value={p.directoryId}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        ) : undefined
      }
      footerSlot={
        <button
          type="button"
          className={styles.createOption}
          onClick={() => {
            onClose();
            onCreateNew();
          }}
        >
          + Create new practice area
        </button>
      }
    >
      {available.map(bpa => (
        <div
          key={bpa.id}
          className={searchModalStyles.resultItem}
          onClick={() => !linking && handleLink(bpa.id)}
        >
          <div className={searchModalStyles.resultInfo}>
            <span className={searchModalStyles.resultName}>{bpa.name}</span>
            <span className={searchModalStyles.resultSubtitle}>{bpa.rankingEntityName}</span>
          </div>
        </div>
      ))}
      {linked.map(bpa => (
        <div
          key={bpa.id}
          className={`${searchModalStyles.resultItem} ${searchModalStyles.resultItemDisabled}`}
        >
          <div className={searchModalStyles.resultInfo}>
            <span className={searchModalStyles.resultName}>{bpa.name}</span>
            <span className={searchModalStyles.resultSubtitle}>{bpa.rankingEntityName}</span>
          </div>
          <span className={searchModalStyles.alreadyLinkedLabel}>Already linked</span>
        </div>
      ))}
    </SearchModal>
  );
}
